import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, mergeMap, Observable, throwError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { UserService } from '../user/user.service';
import { DeviceService } from '../device/device.service';
import { ApiConfiguration } from '../../api/api-configuration';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private userService = inject(UserService);
  private deviceService = inject(DeviceService);
  private apiConfig = inject(ApiConfiguration);

  private static readonly loginUrl = '/login';
  private static readonly skipPaths = [
    '/login',
    '/session/refresh',
    '/session/tenant-login',
  ];

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip requests that are not directed at our own API
    // or that use their own Authorization header (refresh/tenant-login)
    if (
      AuthInterceptor.skipPaths.some((path) => request.url.endsWith(path)) ||
      !request.url.startsWith(this.apiConfig.basePath)
    ) {
      return next.handle(request);
    }

    const requestWithAuthorization = this.addCredentials(request);
    return next.handle(requestWithAuthorization).pipe(
      filter((event) => event instanceof HttpResponse),
      catchError((error) => {
        return this.handleError(error, requestWithAuthorization, next);
      })
    );
  }

  private handleError(
    error: any,
    originalRequest: HttpRequest<any>,
    next: HttpHandler
  ) {
    if (error.status === 401) {
      // Unauthorized. Try to refresh the session.
      return this.userService.refreshSession(next).pipe(
        mergeMap((_) => next.handle(this.addCredentials(originalRequest))),
        catchError((error) => {
          // Only a 401 means "session is dead". A refreshSession() failure with
          // TenantAccessLostError (tenant-login 403: no longer a member of the selected
          // organization) has no status on purpose: the user was already informed and
          // redirected to the org selection, so it is rethrown instead of logging out.
          // The original request is NOT retried (no refresh/logout loop).
          if (error.status === 401) {
            // Still unauthorized. Redirect to login page.
            this.userService.invalidateSession();
            this.redirectToLogin();
            return EMPTY;
          } else {
            return throwError(() => error);
          }
        })
      );
    } else {
      return throwError(() => error);
    }
  }

  /**
   * Redirect browser to clear all caches. Do not use NavController here!
   * Extracted so unit tests can spy on it instead of navigating the test runner.
   */
  private redirectToLogin(): void {
    window.location.href = AuthInterceptor.loginUrl;
  }

  private addCredentials(request: HttpRequest<any>) {
    const deviceHeaders = this.deviceService.getDeviceHeaders();
    const token = this.userService.getJwtToken();
    let headers = request.headers;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    for (const [key, value] of Object.entries(deviceHeaders)) {
      headers = headers.set(key, value);
    }
    return request.clone({ headers });
  }
}
